import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { BottomSheet } from "react-spring-bottom-sheet";
import { isEqual } from "lodash";

import {
  ApiCompetitionJudge,
  ApiInlineUser,
  ApiJudgingGroup,
  ApiRoute,
} from "../../../types";
import Avatar from "../../Avatar";
import ManageRoutesTabNavigation from "../../ManageRoutesTabNavigation";
import MapsSlider from "../../MapsSlider";
import { Pill, Pills } from "../../Pills";
import { RouteCircle } from "../../RouteCircle";
import RoutesList, { RouteName } from "../../RoutesList/RoutesList";
import { IconButton } from "../../button";
import { AddSvg, SettingsSvg, UsersSvg } from "../../icons";
import {
  BottomSheetMenu,
  BottomSheetMenuItem,
} from "../EntriesScreen/EntriesScreen.style";
import * as Style from "./AssignAreasScreen.style";
import useAssignAreasScreen from "./useAssignAreasScreen";

const JudgingGroupInput = ({
  judgingGroup,
  judgingGroups,
  onSelect,
}: {
  route: ApiRoute;
  judgingGroup: { routes: string[]; name: string; uuid: string } | undefined;
  judgingGroups: { routes: string[]; name: string; uuid: string }[];
  onSelect: (judgingGroupUUID: string) => void;
}) => {
  const [isOpen, setOpen] = useState(false);
  return (
    <>
      <Style.Select
        style={{ fontSize: 14, fontWeight: "normal", height: 36 }}
        onClick={() => setOpen(true)}
      >
        {judgingGroup ? judgingGroup.name : "Select judging area..."}
      </Style.Select>
      <BottomSheet open={isOpen} onDismiss={() => setOpen(false)}>
        <BottomSheetMenu>
          {judgingGroups.map((jg, i) => (
            <BottomSheetMenuItem
              key={i}
              value={jg.uuid}
              onClick={() => {
                setOpen(false);
                onSelect(jg.uuid);
              }}
            >
              {jg.name}
            </BottomSheetMenuItem>
          ))}
        </BottomSheetMenu>
      </BottomSheet>
    </>
  );
};

type JudgingGroupSelectorProps = {
  judgingGroups: ApiJudgingGroup[];
  selectedJudgingGroupUUID: string | null;
  onSelect: (judgingGroup?: ApiJudgingGroup | null) => void;
  onAdd: () => void;
};

const JudgingGroupSelector = ({
  judgingGroups,
  selectedJudgingGroupUUID,
  onSelect,
  onAdd,
}: JudgingGroupSelectorProps) => {
  return (
    <Pills>
      {judgingGroups.map((group) => (
        <Pill
          key={group.uuid}
          active={group.uuid === selectedJudgingGroupUUID}
          onClick={() =>
            group.uuid === selectedJudgingGroupUUID
              ? onSelect(null)
              : onSelect(group)
          }
        >
          {group.name}
        </Pill>
      ))}
      <Pill onClick={onAdd}>
        <AddSvg style={{ marginRight: 4 }} />
        Add new judging area
      </Pill>
    </Pills>
  );
};

const AddEditJudgingGroupModal = ({
  judgingGroup,
  onSave,
  onDelete,
  onHide,
  show,
}: {
  judgingGroup?: ApiJudgingGroup;
  onSave: ({ name }: { name: string }) => void;
  onDelete?: () => void;
  onHide: () => void;
  show?: boolean;
}) => {
  const [name, setName] = useState(judgingGroup?.name || "");

  useEffect(() => {
    if (judgingGroup?.name) {
      setName(judgingGroup.name);
    }
  }, [judgingGroup?.name]);

  return (
    <Modal
      show={show}
      onHide={() => {
        setName("");
        onHide();
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {judgingGroup ? "Edit judging area" : "Add a judging area"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="small fw-bold mb-1" htmlFor="name">
          Name
        </label>
        <input
          className="form-control"
          placeholder="e.g. Area 1, Cave Left"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => onSave({ name })}>
          {!judgingGroup ? (
            <AddSvg
              style={{ verticalAlign: "middle", marginTop: -3, marginRight: 6 }}
            />
          ) : null}
          {judgingGroup ? "Save" : "Add"}
        </Button>
        {judgingGroup ? (
          <Button variant="danger" onClick={onDelete}>
            Delete
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

const JudgingGroupInfo = ({
  group,
  onManageJudgesPress,
  onSettingsPress,
}: {
  group: ApiJudgingGroup;
  onManageJudgesPress: () => void;
  onSettingsPress: () => void;
}) => {
  return (
    <Style.JudgingGroupInfoContainer>
      <div className="flex-1">
        <div className="fw-bold">{group.name}</div>
        <div
          className={`small ${group.judges.length === 0 ? "text-muted" : ""}`}
        >
          {group.judges.length === 0 ? <span>No judges assigned</span> : null}
          {group.judges.slice(0, 2).map((judge, i) => (
            <span key={i}>
              {judge.name}
              {i !== group.judges.length - 1 ? ", " : ""}
            </span>
          ))}
          {group.judges.length > 2 ? (
            <span> +{group.judges.length - 2} more</span>
          ) : null}
        </div>
      </div>
      <div className="d-flex">
        <IconButton
          onClick={onManageJudgesPress}
          style={{ backgroundColor: "#e1e1e1" }}
          className="me-2"
        >
          <UsersSvg width={20} height={20} />
        </IconButton>
        <IconButton
          style={{ backgroundColor: "#e1e1e1" }}
          onClick={onSettingsPress}
        >
          <SettingsSvg width={20} height={20} />
        </IconButton>
      </div>
      {/* <Button size="sm" onClick={onManageJudgesPress}>
        Assign Judges
      </Button> */}
    </Style.JudgingGroupInfoContainer>
  );
};

const JudgeSelectionItem = ({
  judge,
  isSelected,
  onToggle,
}: {
  judge: ApiInlineUser;
  isSelected: boolean;
  onToggle: () => void;
}) => {
  return (
    <Style.JudgeSelectionItem onClick={onToggle}>
      <div className="me-3 d-flex align-items-center">
        <input
          type="checkbox"
          className="form-check-input"
          checked={isSelected}
          onChange={onToggle}
          style={{ marginTop: 2, transform: "scale(1.25)" }}
        />
      </div>
      <div className="me-2">
        <Avatar user={judge} size={32} round />
      </div>
      <div>
        <div>{judge.name}</div>
        <div className="small text-muted">{judge.email}</div>
      </div>
    </Style.JudgeSelectionItem>
  );
};

const JudgeSelectionBottomSheet = ({
  group,
  availableJudges,
  onDismiss,
  onSubmit,
  isOpen,
}: {
  group: ApiJudgingGroup;
  availableJudges?: ApiCompetitionJudge[];
  onSubmit: (judgeUUIDs: string[]) => void;
  onDismiss: () => void;
  isOpen: boolean;
}) => {
  const [selectedJudgeUUIDs, setSelectedJudgeUUIDs] = useState(
    group.judges.map((judge) => judge.uuid)
  );
  useEffect(() => {
    if (!isOpen) {
      setSelectedJudgeUUIDs(group.judges.map((judge) => judge.uuid));
    }
  }, [isOpen, group.judges]);

  const onToggleJudgeSelected = (judgeUUID: string, isSelected: boolean) => {
    setSelectedJudgeUUIDs((prev) =>
      isSelected
        ? [...prev, judgeUUID]
        : prev.filter((uuid) => uuid !== judgeUUID)
    );
  };

  const onDoneClick = () => {
    if (
      !isEqual(
        selectedJudgeUUIDs.sort(),
        group.judges.map((judge) => judge.uuid).sort()
      )
    ) {
      onSubmit(selectedJudgeUUIDs);
    }
    onDismiss();
  };

  return (
    <BottomSheet open={isOpen} onDismiss={onDismiss}>
      <div className="fw-bold mb-3 px-2 text-center">Assign Judges</div>
      {availableJudges?.map((judge) => {
        const isSelected = selectedJudgeUUIDs.includes(judge.user.uuid);
        return (
          <JudgeSelectionItem
            key={judge.user.uuid}
            judge={judge.user}
            isSelected={isSelected}
            onToggle={() => onToggleJudgeSelected(judge.user.uuid, !isSelected)}
          ></JudgeSelectionItem>
        );
      })}
      <div className="d-grid m-2">
        <Button variant="primary" className="flex-1" onClick={onDoneClick}>
          Done
        </Button>
      </div>
    </BottomSheet>
  );
};

const AssignAreasScreen = () => {
  const {
    routes,
    maps,
    selectedRouteUUID,
    setSelectedRouteUUID,
    selectedJudgingGroupUUID,
    setSelectedJudgingGroupUUID,
    selectedJudgingGroup,
    competition,
    handleJudgingGroupSelect,
    showAddModal,
    setShowAddModal,
    showEditModal,
    setShowEditModal,
    onAddJudgingGroup: onAddSubmit,
    onEditJudgingGroup: onEditSubmit,
    onDeleteJudgingGroup,
    showJudgesBottomSheet,
    setShowJudgesBottomSheet,
    judges,
    updateJudgingGroupJudges,
  } = useAssignAreasScreen();

  const listRef = useRef<HTMLDivElement>(null);
  const scrollToRouteUUID = (routeUUID: string) => {
    listRef.current
      ?.querySelector(`[data-uuid="${routeUUID}"]`)
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Style.Container>
      <AddEditJudgingGroupModal
        onHide={() => setShowAddModal(false)}
        onSave={(data) => onAddSubmit(data)}
        show={showAddModal}
      />
      <AddEditJudgingGroupModal
        judgingGroup={selectedJudgingGroup}
        onHide={() => setShowEditModal(false)}
        onSave={(data) =>
          selectedJudgingGroupUUID &&
          onEditSubmit(selectedJudgingGroupUUID, data)
        }
        show={showEditModal}
        onDelete={() =>
          selectedJudgingGroupUUID &&
          onDeleteJudgingGroup(selectedJudgingGroupUUID)
        }
      />
      <Style.MapContainer>
        {maps && routes ? (
          <MapsSlider
            maps={maps}
            routes={routes}
            selectedRouteUUID={selectedRouteUUID}
            onRouteClick={(routeUUID) => {
              setSelectedRouteUUID(routeUUID);
              setTimeout(() => scrollToRouteUUID(routeUUID), 10);
            }}
          />
        ) : null}
      </Style.MapContainer>
      <ManageRoutesTabNavigation />
      <Style.AreaSelectorContainer>
        <JudgingGroupSelector
          selectedJudgingGroupUUID={selectedJudgingGroupUUID}
          judgingGroups={competition?.judgingGroups || []}
          onSelect={(judgingGroup) =>
            setSelectedJudgingGroupUUID(judgingGroup ? judgingGroup.uuid : null)
          }
          onAdd={() => setShowAddModal(true)}
        />
      </Style.AreaSelectorContainer>
      {selectedJudgingGroup ? (
        <JudgingGroupInfo
          group={selectedJudgingGroup}
          onManageJudgesPress={() => setShowJudgesBottomSheet(true)}
          onSettingsPress={() => setShowEditModal(true)}
        />
      ) : null}
      {selectedJudgingGroup ? (
        <JudgeSelectionBottomSheet
          isOpen={showJudgesBottomSheet}
          group={selectedJudgingGroup}
          availableJudges={judges}
          onSubmit={(uuids: string[]) =>
            updateJudgingGroupJudges(selectedJudgingGroup, uuids)
          }
          onDismiss={() => setShowJudgesBottomSheet(false)}
        />
      ) : null}
      <Style.RouteListContainer>
        <RoutesList
          ref={listRef}
          items={
            routes?.map((route) => ({
              route,
              judgingGroup: competition?.judgingGroups.find((jg) =>
                jg.routes.includes(route.uuid)
              ),
            })) || []
          }
          headerRightText="Judging Area"
          selectedRouteUUID={selectedRouteUUID}
          onSelectRoute={setSelectedRouteUUID}
          renderEmpty={() => (
            <div className="text-center mt-4 text-muted">
              No routes assigned. Return to the main list to assign routes to
              this area
            </div>
          )}
          renderItemContents={({ route, judgingGroup }) => {
            return (
              <>
                <RouteCircle
                  colors={(route.colors || "#FFFFFF").split(":")}
                  className="me-2"
                />
                <RouteName>{route.circuitNumber}</RouteName>
                {competition?.judgingGroups.length ? (
                  <div>
                    {route.uuid === selectedRouteUUID ? (
                      <JudgingGroupInput
                        route={route}
                        judgingGroup={judgingGroup}
                        judgingGroups={competition.judgingGroups}
                        onSelect={(judgingGroupUUID: string) =>
                          handleJudgingGroupSelect({
                            routeUUID: route.uuid,
                            judgingGroupUUID,
                          })
                        }
                      />
                    ) : (
                      <div className="fw-normal text-end">
                        {judgingGroup?.name || "–"}
                      </div>
                    )}
                  </div>
                ) : null}
              </>
            );
          }}
        />
      </Style.RouteListContainer>
    </Style.Container>
  );
};

export default AssignAreasScreen;
